import { useEffect, useState } from 'react';
import { Box, CloseButton, HStack, Spinner, Text } from '@chakra-ui/react';
import { Check } from 'lucide-react';
import { WS_EVENTS } from '@twinden/shared';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useT } from '../../i18n';

// Right after joining with an "Add a device" link, this device has no chat
// keys yet; the device that showed the link is handing them over (D138).
const FLAG = 'twinden:gettingKeys';
// Stop saying "getting" if nothing arrived within an hour.
const MAX_AGE_MS = 60 * 60 * 1000;

/** Called when a device joins with an add-device link. */
export function markGettingKeys() {
  try {
    localStorage.setItem(FLAG, String(Date.now()));
  } catch {
    // Private mode: the notice just doesn't show.
  }
}

function waiting(): boolean {
  try {
    const since = Number(localStorage.getItem(FLAG));
    return since > 0 && Date.now() - since < MAX_AGE_MS;
  } catch {
    return false;
  }
}

function clear() {
  try {
    localStorage.removeItem(FLAG);
  } catch {
    // Nothing to clear.
  }
}

export function GettingKeysNotice() {
  const [state, setState] = useState<'getting' | 'done' | null>(() =>
    waiting() ? 'getting' : null
  );
  const { onEvent } = useWebSocket();
  const t = useT();

  useEffect(() => {
    if (state !== 'getting') return;
    return onEvent(({ event }) => {
      if (event !== WS_EVENTS.KEYS_SHARED) return;
      clear();
      setState('done');
    });
  }, [state, onEvent]);

  useEffect(() => {
    if (state !== 'done') return;
    const timer = setTimeout(() => setState(null), 5000);
    return () => clearTimeout(timer);
  }, [state]);

  if (!state) return null;
  return (
    <Box px={4} pb={3}>
      <HStack
        spacing={3}
        px={4}
        py={3}
        borderRadius="18px"
        bg="accent.tint"
        role="status"
      >
        {state === 'getting' ? (
          <Spinner size="sm" color="accent" />
        ) : (
          <Box color="accent">
            <Check size={18} />
          </Box>
        )}
        <Text flex="1" fontSize="15px" fontWeight={600}>
          {state === 'getting'
            ? t('chat.keys.getting')
            : t('chat.keys.unlocked')}
        </Text>
        {state === 'getting' && (
          <CloseButton
            size="sm"
            aria-label={t('chat.keys.hide')}
            onClick={() => {
              clear();
              setState(null);
            }}
          />
        )}
      </HStack>
    </Box>
  );
}
